import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { NgxSpinner } from 'ngx-spinner/lib/ngx-spinner.enum';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { SubSink } from 'subsink';
import { AuthenticationService } from '../services/authentication.service';
import { CompanyService } from '../services/company.service';
import { PpsUtilityService } from '../services/pps-utility.service';
import { Company, CompanyRepresentative } from '../shared/models/company';
import { ToastService } from '../shared/toast/toast.service';

@Component({
  selector: 'pps-client-management',
  templateUrl: './client-management.component.html',
  styles: [
  ],
})
export class ClientManagementComponent implements OnInit, OnDestroy {
  private subs = new SubSink();

  smallScreen: boolean = false;
  
  items: MenuItem[] = [
    {label: "Settings", icon: "pi-fw fas fa-tools"},
    {label: "Manage Client", icon: "pi-fw fas fa-user-cog"},
  ];

  companies: any[] = [];
  companyGridHeaders: any[];
  selectedCompany: any = null;
  companyContext:any = {};
  private companyGridApi;
  companyOptions = {
    getRowId: param => param.data.id,
    defaultColDef: {
      enableCellChangeFlash: true
    }
  };

  companyReps: any[] = [];
  repGridHeaders: any[];
  selectedRep: any = null;
  repContext:any = {};
  private repGridApi;
  repOptions = {
    getRowId: param => param.data.id,
    defaultColDef: {
      enableCellChangeFlash: true
    }
  };

  entityLoading: boolean = false;

  constructor(private _companyService: CompanyService,
              private _confirm: ConfirmationService,
              private _toast: ToastService,
              private _spinner: NgxSpinnerService,
              private _util: PpsUtilityService,
              private breakpointObserver: BreakpointObserver) { 
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
        console.log("smallScreen? " + this.smallScreen);
      });    
  }
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.initialize();
  }

  private initialize(){
    this.companyContext = { componentParent: this };
    this.companyGridHeaders = this.getGridHeader("Client Name", this.companyNameChanged);
    this.loadCompanies();

    this.repContext = { componentParent: this };
    this.repGridHeaders = this.getGridHeader("Client Representatives", this.companyRepChanged);
  }

  onCompanyGridReady(params) {
    this.companyGridApi = params.api;
    this.autoSelectCompany();
  }

  onRepGridReady(params){
    this.repGridApi = params.api;
  }

  onCompanyRowClicked(evt){
    let _this = evt.context.componentParent;
    let selectedRows = _this.companyGridApi.getSelectedRows();
    let selected = selectedRows.length === 1 ? selectedRows[0] : null;
    if (_this.selectedCompany?.id !== selected?.id){
      _this.selectedCompany = selected;
      _this.loadCompanyReps(_this.selectedCompany.id);
    }
  }

  onRepRowClicked(evt){
    let _this = evt.context.componentParent;
    var selectedRows = _this.repGridApi.getSelectedRows();
    _this.selectedRep = selectedRows.length === 1 ? selectedRows[0] : null;
  }

  onCompanyCellKeyDown(evt){
    this.onDelete(evt, (_this) => {
      _this.companyGridApi.stopEditing(true);
      _this.deleteCompany();
    });
  }

  onRepCellKeyDown(evt){
    this.onDelete(evt, (_this) => {
      _this.repGridApi.stopEditing(true);
      _this.deleteCompanyRep();
    })
  }

  loadCompanies(){
    this._spinner.show("companySpinner");
    this.entityLoading = true;
    this.companies.length = 0;
    this.subs.sink = this._companyService.getAll()
      .subscribe(
        apiResponse => {
          if (apiResponse.succeed){
            this.companies = apiResponse.result;
            this.companyReps = [];
            this._toast.succeeded(`${this.companies.length} client companies loaded`);
          }else{
            this._toast.failed(`Failed to load positions: ${apiResponse.errors.join(";")}`);
          }
        }, 
        _error => {
          console.log(_error);
        })
        .add(()=>{
          this._spinner.hide("companySpinner");
          this.entityLoading = false;
        });
  }

  loadCompanyReps(companyId?: number){
    if (companyId === null){
      this.companyReps = [];
      return;
    }

    this.entityLoading = true;
    this.companyReps.length = 0;
    this.subs.sink = this._companyService.getCompanyRepresentatives(companyId)
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          this.companyReps = apiResponse.result;
          this.selectedRep = null;
          this._toast.succeeded(`${this.companyReps.length} client representatives loaded`);
        }else{
          this._toast.failed(`Failed to load representative: ${apiResponse.errors.join(";")}`);
        }
      }, _error => {
        console.log(_error);
      })
      .add(()=>{
        this.entityLoading = false;
      });
  }

  addCompany(){
    this._spinner.show("companySpinner");

    let newCompany = new Company();
    newCompany.name = this._util.getNewName("New Owner", 0, this.companies, (c, name) => c.name === name);
    this.subs.sink = this._companyService.add(newCompany)
      .subscribe(res => {
        this.companies.unshift(res.result);
        this.companyGridApi.setRowData(this.companies);
        let rowNode = this.companyGridApi.getRowNode(res.result.id);
        if (rowNode){
          rowNode.setSelected(true);
          this.selectedRep = null;
          this.selectedCompany = res.result;
          this.loadCompanyReps(res.result.id);
          this.companyGridApi.flashCells({rowNodes: [rowNode]});
          window.setTimeout( () =>{
            this.companyGridApi.startEditingCell({rowIndex: rowNode.rowIndex, colKey: "name"});
          }, 1000);
        }
      })
      .add(() => this._spinner.hide("companySpinner"));
  }

  addCompanyRep(){
    if (this.selectedCompany === null){
      return;
    }

    this._spinner.show("companySpinner");
    let newCompanyRep = new CompanyRepresentative();
    newCompanyRep.name = this._util.getNewName("New Representative", 0, this.companyReps, (c, name) => c.name === name);
    this.subs.sink = this._companyService.addCompanyRepresentative(this.selectedCompany.id, newCompanyRep)
      .subscribe(res => {
        this.companyReps.unshift(res.result);
        this.repGridApi.setRowData(this.companyReps);
        let rowNode = this.repGridApi.getRowNode(res.result.id);
        if (rowNode){
          rowNode.setSelected(true);
          this.selectedRep = res.result;
          this.repGridApi.flashCells({rowNodes: [rowNode]});
          window.setTimeout( () =>{
            this.repGridApi.startEditingCell({rowIndex: rowNode.rowIndex, colKey: "name"});
          }, 1000);
        }
      })
      .add(() => this._spinner.hide("companySpinner"));
  }

  deleteCompany(){
    if (this.selectedCompany === null){
      return;
    }

    this._confirm.confirm({
      header: `Delete Client`,
      message: `Are you sure that you want to delete client [${this.selectedCompany.name}]?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => this._deleteCompany()
    });
  }

  _deleteCompany(){
    this._spinner.show("companySpinner");
    this.subs.sink = this._companyService.delete(this.selectedCompany.id)
      .subscribe(res => {
        if (res.succeed){
          var foundIndex = this.companies.findIndex(x => x.id == this.selectedCompany.id);
          if (foundIndex !== -1 ){
            this.companies.splice(foundIndex, 1);
            this.companyGridApi.setRowData(this.companies);
            this._toast.succeeded(`[${this.selectedCompany.name}] deleted!`);
            this.selectedCompany = null;
            this.selectedRep = null;
            this.loadCompanyReps(null);
          }else{
            this._toast.failed(`Delete [${this.selectedCompany.name}] failed!`);
          }
        }else{
          this._toast.failed(`Failed to delete [${this.selectedCompany.name}]: ${res.errors.join("|")}`);
        }
      })
      .add(() => this._spinner.hide("companySpinner"));
  }

  deleteCompanyRep(){
    if (this.selectedRep === null){
      return;
    }

    this._confirm.confirm({
      header: `Delete Representative`,
      message: `Are you sure that you want to delete representative [${this.selectedRep.name}]?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => this._deleteCompanyRep()
    });
  }

  _deleteCompanyRep(){
    this._spinner.show("companySpinner");
    this.subs.sink = this._companyService.deleteCompanyRepresentative(this.selectedRep.id)
      .subscribe(res => {
        if (res.succeed){
          var foundIndex = this.companyReps.findIndex(x => x.id == this.selectedRep.id);
          if (foundIndex !== -1 ){
            this.companyReps.splice(foundIndex, 1);
            this.repGridApi.setRowData(this.companyReps);
            this._toast.succeeded(`[${this.selectedRep.name}] deleted!`);
            this.selectedRep = null;
          }else{
            this._toast.failed(`Delete [${this.selectedRep.name}] failed!`);
          }
        }else{
          this._toast.failed(`Failed to delete [${this.selectedRep.name}]: ${res.errors.join("|")}`);
        }
      })
      .add(() => this._spinner.hide("companySpinner"));
  }
  
  private getGridHeader(headerName, onCellValueChange){
    let cols = [
      {
        headerName: headerName,
        field:'name',
        filter: true,
        sortable: true,
        flex: 1,
        editable: true,
        floatingFilter: true,
        onCellValueChanged: onCellValueChange
      }
    ];

    return cols;
  }

  private companyNameChanged(params){
    let _this = params.context.componentParent;
    let company = {id: params.data.id, name: params.data.name, active: true};

    _this._spinner.show("companySpinner");
    _this.subs.sink = _this._companyService.update(company)
      .subscribe(res => {
        if (res.succeed){
          _this._toast.succeeded(`Client name updated`);
        }else{
          _this._toast.failed(`Client name update failed: ${res.errors.join("|")}`);
        }
      })
      .add(() => _this._spinner.hide("companySpinner"));
  }  

  private companyRepChanged(params){
    let _this = params.context.componentParent;
    let companyRep = {id: params.data.id, name: params.data.name, active: true};
    let companyId = _this.selectedCompany.Id;

    _this._spinner.show("companySpinner");
    _this.subs.sink = _this._companyService.updateCompanyRepresentative(companyId, companyRep)
      .subscribe(res => {
        if (res.succeed){
          _this._toast.succeeded(`Representative name updated`);
        }else{
          _this._toast.failed(`Representative name update failed: ${res.errors.join("|")}`);
        }
      })
      .add(() => _this._spinner.hide("companySpinner"));
  }  

  // select the previous selected or the first company
  private autoSelectCompany(){
    if (this.companies.length > 0 && this.companyGridApi){
      this.selectedCompany = this.selectedCompany || this.companies[0];
      let rowNode = this.companyGridApi.getRowNode(this.selectedCompany.id);
      if (rowNode){
        rowNode.setSelected(true, true);
        this.loadCompanyReps(this.selectedCompany.id);
      }
    }
  }

  private onDelete(evt, action){
    if ( evt.event.key === "Delete"){
      if (evt.event.path[0].nodeName === "DIV"){
        action(this);
      }
    }
  }
}
